import type { PolicySection } from '../types/policy'

export const saleTermsMeta = {
  company: 'Fem Entity Inc.',
  title: 'Practitioner Terms and Conditions of Sale',
  version: '1.0',
  lastUpdated: 'September 2026',
  summary:
    'How practitioner subscription plans are sold, billed, renewed, and cancelled, and how payments for bookings made through Fem Entity are handled.',
  pdfUrl:
    'https://cdn.magicpatterns.com/uploads/wkSdGxBcR8r5UQi9ku34vY/Fem_Entity_Practitioner_Terms_and_Conditions_of_Sale_2026.pdf',
  email: 'hello@fementity.ca',
  preamble: [
    'These Practitioner Terms and Conditions of Sale (the “Practitioner Sale Terms”) are a legal agreement between the practitioner or organization accepting them (“you” or the “Practitioner”) and Fem Entity Inc. (“Fem Entity,” “we,” “us,” or “our”). They govern purchases of practitioner subscription plans and related Platform services, as well as payment obligations connected with practitioner bookings facilitated through the Fem Entity mobile application, website, and related services (the “Platform”).',
    'These terms supplement any practitioner, platform, independent contractor, commitment, or other written agreement between you and Fem Entity. Review the plan, price, billing cycle, fees, renewal terms, and any promotional terms shown and accepted at checkout or in your signed agreement before subscribing.',
  ],
}

export const saleTermsSections: PolicySection[] = [
  {
    id: 'authority-eligibility',
    number: 1,
    title: 'Authority Eligibility and Account Information',
    blocks: [
      {
        kind: 'p',
        text: 'You confirm that you are at least 18 years old, have legal capacity to enter into these terms, and, if accepting for an organization, have authority to bind that organization. You must provide accurate and current identity, business, contact, tax, billing, and payment information.',
      },
      {
        kind: 'p',
        text: 'The Platform is offered for practitioner operations in Canada excluding Quebec, subject to the geographic, credential, professional, insurance, and scope-of-practice requirements applicable to you and your services.',
      },
    ],
  },
  {
    id: 'practitioner-agreements',
    number: 2,
    title: 'Relationship to Practitioner Agreements',
    blocks: [
      {
        kind: 'p',
        text: 'Your applicable signed practitioner agreement governs your eligibility, verification, professional responsibilities, Platform conduct, service delivery, records, privacy obligations, and other matters addressed there. These Practitioner Sale Terms govern subscription and payment matters unless the signed agreement expressly states a different rule.',
      },
      {
        kind: 'p',
        text: 'If a conflict exists, the following order applies for the matter in conflict: first, a signed agreement that expressly overrides these Practitioner Sale Terms; second, specific pricing or promotional terms shown and accepted for your purchase; third, these Practitioner Sale Terms; and fourth, the general Terms and Conditions of Use. Mandatory law always applies.',
      },
    ],
  },
  {
    id: 'plans-features-seats',
    number: 3,
    title: 'Plans Features and Seats',
    blocks: [
      {
        kind: 'p',
        text: 'The current practitioner plans, included features, usage limits, and any seat entitlements are described in the purchase flow or your applicable signed agreement. Plan features may differ by tier. You are responsible for selecting an appropriate plan and for administering authorized users or seats associated with your account.',
      },
      {
        kind: 'p',
        text: 'Fem Entity may modify future plan features or availability. We will provide reasonable notice where a material change affects a current paid subscription, subject to the terms accepted for that subscription and applicable law.',
      },
    ],
  },
  {
    id: 'subscription-billing',
    number: 4,
    title: 'Subscription Price and Recurring Billing',
    blocks: [
      {
        kind: 'p',
        text: 'Your subscription price, billing cycle, renewal date, and any applicable promotional or founding terms are the terms shown and accepted at checkout or set out in your applicable signed practitioner agreement. All amounts are in Canadian dollars unless stated otherwise.',
      },
      {
        kind: 'p',
        text: 'Subscriptions automatically renew for successive periods matching the disclosed billing cycle until cancelled in accordance with Section 10. By subscribing, you authorize Fem Entity and its payment processor to charge the payment method on file on each renewal date for the subscription price, applicable taxes, and other charges you accepted.',
      },
      {
        kind: 'p',
        text: 'Fem Entity will not rely on an undisclosed founding discount, free period, locked rate, or discount duration. If different records describe a founding offer differently, the specific term you accepted at checkout or in your signed agreement controls, subject to mandatory law.',
      },
    ],
  },
  {
    id: 'platform-fees',
    number: 5,
    title: 'Platform Fees and Practitioner Service Pricing',
    blocks: [
      {
        kind: 'p',
        text: 'You set the prices for your own practitioner services. Fem Entity does not set or discount your service prices. You are responsible for setting prices with all applicable Fem Entity platform fees and payment-processing charges in mind, while complying with professional, tax, advertising, consumer-protection, and other legal obligations.',
      },
      {
        kind: 'p',
        text: 'The amount or method for calculating an applicable Fem Entity platform fee will be shown and accepted in the Platform, at checkout, in a fee schedule incorporated into your agreement, or in your applicable signed practitioner agreement before the fee applies. Fem Entity will not charge an undisclosed platform fee.',
      },
    ],
  },
  {
    id: 'payment-processing',
    number: 6,
    title: 'Payment Processing for Practitioner Services',
    blocks: [
      {
        kind: 'p',
        text: 'Fem Entity facilitates payment processing for practitioner services through Stripe. A user’s payment method is collected at booking, and the applicable service charge is charged after the appointment. The user may also authorize charges under your cancellation and no-show policy if that policy was displayed or otherwise made available before booking confirmation.',
      },
      {
        kind: 'p',
        text: 'You must maintain an eligible connected payment account, complete required onboarding or verification, and provide accurate payout, business, beneficial ownership, and tax information requested by Stripe or Fem Entity. Payment processing, transfers, settlement timing, reserves, refunds, disputes, and chargebacks may be affected by Stripe’s terms and by the technical payment configuration used for the transaction.',
      },
      {
        kind: 'p',
        text: 'These Practitioner Sale Terms do not state that funds flow directly to you or designate either party as merchant of record. The applicable Stripe Connect architecture must be confirmed separately against the implemented payment flow.',
      },
    ],
  },
  {
    id: 'cancellations-no-shows',
    number: 7,
    title: 'Cancellations No Shows and Refunds for Your Services',
    blocks: [
      {
        kind: 'p',
        text: 'You set your own cancellation policy and any late-cancellation or no-show fee, subject to applicable law and professional obligations. You must make that policy available through the Platform before the user confirms the booking. You may not apply a later or undisclosed policy retroactively.',
      },
      {
        kind: 'p',
        text: 'If you cancel an appointment, the user will not be charged for the cancelled service or a related cancellation or no-show fee. You are responsible for ordinary service-related billing and refund decisions. Fem Entity may assist with technical or payment-processing issues and may take steps reasonably necessary to implement an authorized refund, correct an error, respond to a dispute or chargeback, comply with payment-network requirements, or comply with law.',
      },
    ],
  },
  {
    id: 'taxes',
    number: 8,
    title: 'Taxes',
    blocks: [
      {
        kind: 'p',
        text: 'Subscription fees and platform fees are exclusive of applicable taxes unless the purchase flow or agreement states otherwise. You authorize Fem Entity to charge taxes it is required to collect.',
      },
      {
        kind: 'p',
        text: 'You are responsible for determining, collecting, reporting, and remitting taxes applicable to your practitioner services and business, except to the extent Fem Entity or a payment processor is legally required to do so. Fem Entity does not provide tax advice.',
      },
    ],
  },
  {
    id: 'failed-payments',
    number: 9,
    title: 'Failed Payments and Account Consequences',
    blocks: [
      {
        kind: 'p',
        text: 'If a subscription, platform fee, or other amount you owe cannot be charged, you authorize Fem Entity and its payment processor to retry the payment method and request an updated payment method. The amount remains payable.',
      },
      {
        kind: 'p',
        text: 'Fem Entity may restrict paid features, pause booking or payout functionality where operationally or legally appropriate, suspend the account, or terminate the subscription if an overdue amount remains unpaid after reasonable notice and an opportunity to correct the failure. Any action will remain subject to your applicable signed agreement, payment-provider requirements, and mandatory law. Account suspension does not eliminate amounts already owing.',
      },
    ],
  },
  {
    id: 'cancellation-renewal-refunds',
    number: 10,
    title: 'Cancellation Renewal and Refunds',
    blocks: [
      {
        kind: 'p',
        text: 'You may cancel a subscription using available account controls or by contacting hello@fementity.ca. To avoid the next renewal charge, cancel before the renewal date shown in your account or purchase terms. Unless your accepted checkout terms or signed agreement state otherwise, cancellation takes effect at the end of the current paid billing period and access continues until then.',
      },
      {
        kind: 'p',
        text: 'Subscription and platform fees already paid are non-refundable and will not be prorated for an unused portion of a billing period, except where required by law or expressly stated in the terms accepted for the purchase. Cancelling and later restarting may result in then-current pricing unless your accepted promotional terms or signed agreement expressly preserve a different rate.',
      },
    ],
  },
  {
    id: 'price-term-changes',
    number: 11,
    title: 'Price and Term Changes',
    blocks: [
      {
        kind: 'p',
        text: 'Fem Entity may change subscription prices, platform fees, or these Practitioner Sale Terms prospectively. We will provide reasonable advance notice of a material change before it applies to a renewal or future transaction. A change will not override a rate or term that Fem Entity expressly agreed to preserve in your accepted checkout terms or signed agreement.',
      },
      {
        kind: 'p',
        text: 'If you do not agree to a change that applies at renewal, cancel before the renewal date. Continued use after the effective date constitutes acceptance only to the extent permitted by applicable law and the applicable agreement.',
      },
    ],
  },
  {
    id: 'records-corrections',
    number: 12,
    title: 'Records and Corrections',
    blocks: [
      {
        kind: 'p',
        text: 'Electronic invoices, receipts, fee statements, and transaction records may be provided through the Platform or by email. Review them promptly. Contact hello@fementity.ca about a technical Platform or payment-processing issue. You remain responsible for addressing ordinary service billing or refund concerns with the user, while cooperating with Fem Entity where a Platform correction, refund, dispute, or chargeback must be processed.',
      },
    ],
  },
  {
    id: 'independent-business',
    number: 13,
    title: 'Independent Business Relationship',
    blocks: [
      {
        kind: 'p',
        text: 'You operate an independent business and provide practitioner services independently. Nothing in these Practitioner Sale Terms creates employment, agency, partnership, joint venture, fiduciary, franchise, or representative authority between you and Fem Entity. You have no authority to bind Fem Entity or make commitments on its behalf.',
      },
    ],
  },
  {
    id: 'governing-law',
    number: 14,
    title: 'Governing Law and Mandatory Rights',
    blocks: [
      {
        kind: 'p',
        text: 'These Practitioner Sale Terms are governed by the laws of Nova Scotia and the federal laws of Canada applicable there, without regard to conflict-of-law rules. Any non-waivable right, remedy, professional obligation, or statutory requirement continues to apply.',
      },
    ],
  },
  {
    id: 'entire-agreement',
    number: 15,
    title: 'Entire Agreement and Severability',
    blocks: [
      {
        kind: 'p',
        text: 'Together with the documents identified in Section 2, these Practitioner Sale Terms form the entire agreement concerning their subject matter and replace prior communications on that subject, except for fraud or misrepresentation and subject to mandatory law. If a provision is invalid or unenforceable, it will be limited or removed to the minimum extent necessary and the remaining provisions will continue in effect.',
      },
    ],
  },
  {
    id: 'contact',
    number: 16,
    title: 'Contact',
    blocks: [
      {
        kind: 'p',
        text: 'Fem Entity Inc. is a federal corporation under the Canada Business Corporations Act and is extra-provincially registered in Nova Scotia.',
      },
      {
        kind: 'contact',
        title: 'Fem Entity Inc.',
        lines: [
          { value: 'Halifax, Nova Scotia, Canada' },
          { label: 'Email', value: 'hello@fementity.ca', href: 'mailto:hello@fementity.ca' },
          { label: 'Website', value: 'www.fementity.ca', href: 'https://www.fementity.ca' },
        ],
      },
    ],
  },
]
